/**
 * Client for the Cold Email Agent (Marketing) — crm-api's /cold_emails endpoints.
 *
 * The feature physically lives under `src/crm/` (the walled CRM sub-app, so it
 * can talk to crm-api) but is surfaced in the Marketing section at
 * `/marketing/cold-email`. All calls go through the shared CRM axios `api`
 * instance, which auto-prefixes `/api/v1` and attaches the short-TTL CRM token.
 */
import api from "@crm-src/lib/api";

export type ColdEmailStatus =
  | "pending_generation"
  | "generating"
  | "pending_review"
  | "approved"
  | "sending"
  | "sent"
  | "failed"
  | "skipped";

export type ColdEmailRecipientStatus = "pending" | "sent" | "failed" | "skipped";

export interface ColdEmailRecipient {
  id: number;
  name: string | null;
  surname: string | null;
  email: string;
  job_title: string | null;
  company: string | null;
  status: ColdEmailRecipientStatus;
  sent_at: string | null;
  error: string | null;
}

export interface ColdEmailBatch {
  id: number;
  company_name: string;
  company_domain: string | null;
  industry: string | null;
  subject: string | null;
  body: string | null;
  status: ColdEmailStatus;
  generation_error: string | null;
  recipients_count: number;
  sent_count: number;
  failed_count: number;
  approved_by: string | null;
  approved_at: string | null;
  last_generated_at: string | null;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
  recipients?: ColdEmailRecipient[];
}

export interface ColdEmailStats {
  total: number;
  pending_generation: number;
  generating: number;
  pending_review: number;
  approved: number;
  sending: number;
  sent: number;
  failed: number;
  skipped: number;
  recipients_total: number;
  sent_recipients: number;
}

export interface ColdEmailSettings {
  steering: string | null;
  generation_token: number;
  from_name: string;
  from_address: string;
}

export interface ColdEmailSettingsPatch {
  steering?: string;
  from_name?: string;
  from_address?: string;
}

export interface ColdEmailListResponse {
  batches: ColdEmailBatch[];
  stats: ColdEmailStats;
  settings: ColdEmailSettings;
  auto_send_enabled: boolean;
  pagination: {
    current_page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
}

export interface ColdEmailBuildResult {
  companies_created: number;
  recipients_created: number;
  companies_skipped: number;
  stats: ColdEmailStats;
}

export async function listColdEmails(
  params: { status?: string; search?: string; page?: number } = {},
): Promise<ColdEmailListResponse> {
  const res = await api.get("/cold_emails", { params });
  return res.data as ColdEmailListResponse;
}

export async function getColdEmail(id: number): Promise<ColdEmailBatch> {
  const res = await api.get(`/cold_emails/${id}`);
  return (res.data as { batch: ColdEmailBatch }).batch;
}

export async function coldEmailAudiencePreview(): Promise<{ contacts: number; companies: number }> {
  const res = await api.get("/cold_emails/audience_preview");
  return res.data as { contacts: number; companies: number };
}

export async function buildColdEmailQueue(): Promise<ColdEmailBuildResult> {
  const res = await api.post("/cold_emails/build");
  return res.data as ColdEmailBuildResult;
}

// scope: "undrafted" (default — draft the un-drafted), "drafted" (re-draft the
// pending_review + failed ones = Regenerate all), or "all".
export async function generatePendingColdEmails(
  opts: { limit?: number; scope?: "undrafted" | "drafted" | "all" } = {},
): Promise<{ queued: number }> {
  const res = await api.post("/cold_emails/generate_pending", {
    ...(opts.limit ? { limit: opts.limit } : {}),
    ...(opts.scope ? { scope: opts.scope } : {}),
  });
  return res.data as { queued: number };
}

// Single-batch (re)generation is synchronous: crm-api calls the AI gateway
// inline, which can take a while — override the default 10s axios timeout.
export async function generateColdEmail(id: number): Promise<ColdEmailBatch> {
  const res = await api.post(`/cold_emails/${id}/generate`, {}, { timeout: 130000 });
  return (res.data as { batch: ColdEmailBatch }).batch;
}

export async function updateColdEmail(
  id: number,
  data: { subject?: string; body?: string },
): Promise<ColdEmailBatch> {
  const res = await api.patch(`/cold_emails/${id}`, { cold_email: data });
  return (res.data as { batch: ColdEmailBatch }).batch;
}

export async function approveColdEmail(id: number): Promise<ColdEmailBatch> {
  const res = await api.post(`/cold_emails/${id}/approve`);
  return (res.data as { batch: ColdEmailBatch }).batch;
}

export async function skipColdEmail(id: number): Promise<ColdEmailBatch> {
  const res = await api.post(`/cold_emails/${id}/skip`);
  return (res.data as { batch: ColdEmailBatch }).batch;
}

export async function deleteColdEmail(id: number): Promise<void> {
  await api.delete(`/cold_emails/${id}`);
}

// Send the current (possibly unsaved) draft to ONE address so the operator can
// preview it — like the Email Campaigns test send. Does not touch the batch.
export async function sendColdEmailTest(
  id: number,
  params: { email: string; recipient_name?: string; subject?: string; body?: string },
): Promise<void> {
  await api.post(`/cold_emails/${id}/test_email`, params);
}

export async function bulkDeleteColdEmails(
  params: { ids?: number[]; status?: string },
): Promise<{ deleted: number }> {
  const res = await api.post("/cold_emails/bulk_delete", params);
  return res.data as { deleted: number };
}

// Halt an in-flight bulk drafting run (bumps the generation token so queued
// jobs no-op). Safe to call even when nothing is drafting.
export async function stopColdEmailGeneration(): Promise<void> {
  await api.post("/cold_emails/stop_generation");
}

export async function getColdEmailSettings(): Promise<ColdEmailSettings> {
  const res = await api.get("/cold_emails/settings");
  return (res.data as { settings: ColdEmailSettings }).settings;
}

// Update generator + sender settings. Only the keys you pass are changed
// (steering, from_name, from_address). Passing steering: "" clears it.
export async function updateColdEmailSettings(patch: ColdEmailSettingsPatch): Promise<ColdEmailSettings> {
  const res = await api.patch("/cold_emails/settings", patch);
  return (res.data as { settings: ColdEmailSettings }).settings;
}
